import clsx from 'clsx';
import { ChangeEvent, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getAgeFromBirthyear, getBirthyearFromAge, writeUserToStorage } from '@vestico/model';

import { FormValidationContext } from '../../CommonAppContext';
import { EntryField, EntryFieldConfiguration } from '../../components/EntryField';
import { useIsMounted } from '../../hooks/mounted';
import { getAgeConfiguration, updateTranslationForConfiguration } from '../../model/configurations';
import { StepProps } from '../../model/step';
import { UserContext } from '../../model/userContext';
import StepContainer from '../StepContainer';
import style from './AgeForm.module.css';

export const AgeForm = (props: StepProps) => {
  const { user } = useContext(UserContext);
  const formValidationContext = useContext(FormValidationContext);
  const mounted = useIsMounted();
  const { t } = useTranslation('shared');
  const [submitting, setSubmitting] = useState(false);

  useMemo(() => {
    const isSubmittingFromExternally = props.submitting;
    if (isSubmittingFromExternally) {
      setSubmitting(isSubmittingFromExternally);
    }
    return isSubmittingFromExternally;
  }, [props.submitting]);

  const [ageConfiguration, setAgeConfiguration] = useState(getAgeConfiguration());

  const ageError = useMemo(() => !!(mounted.current && !ageConfiguration.value), [ageConfiguration, submitting, mounted]);
  const formValid = useMemo(() => !ageError, [ageError]);

  ageConfiguration.didUpdateConfig = (newConfig: EntryFieldConfiguration) => {
    setAgeConfiguration(newConfig);
  };

  useEffect(() => {
    setAgeConfiguration(updateTranslationForConfiguration(ageConfiguration, undefined, getAgeConfiguration));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Don't observe changes to ageConfig as it'll result in an infinte loop
  }, [t]);

  useMemo(() => {
    if (!user) {
      return;
    }

    ageConfiguration.value = getAgeFromBirthyear(user.birthyear);
  }, [user]);

  const updateUserProperties = useCallback(() => {
    if (!user) {
      console.error('no user fetched');
      return;
    }

    user.birthyear = getBirthyearFromAge(ageConfiguration.value);
  }, [ageConfiguration.value, user]);

  useLayoutEffect(() => {
    if (!submitting) {
      return;
    }

    formValidationContext.updateStep(props.path, formValid);
    ageConfiguration.showErrorIfNeeded = true;

    if (!formValid) {
      setSubmitting(false);
      return;
    }

    if (!user) {
      console.error('missing user');
      return;
    }

    // store data

    updateUserProperties();
    writeUserToStorage(user);
    ageConfiguration.showErrorIfNeeded = false;
    setSubmitting(false);
    formValidationContext.submit();
  }, [ageConfiguration, formValid, formValidationContext, props.path, submitting, updateUserProperties, user]);

  const onFormSubmit = useCallback((e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
  }, []);

  return (
    <StepContainer title={props.title}>
      <form
        className={clsx(style.formLayout)}
        onSubmit={onFormSubmit}
        // skipcq JS-0455
        // eslint-disable-next-line react/no-unknown-property
        body-scroll-lock-ignore="true"
      >
        <EntryField configuration={ageConfiguration} />
      </form>
    </StepContainer>
  );
};

export default AgeForm;
